import {
  type BaseOption,
  branchesService,
  cargoTypesService,
  carrierServicesService,
  carriersService,
  companyUsersService,
  currenciesService,
  pricingTypesService,
  tariffTypesService,
} from '@/modules/entities';
import { formatDate } from '@/utils/date';
import { getSelectedCompanyId } from '@/utils/user';

import type {
  ContractDocumentItem,
  ContractDocuments,
  ContractDocumentsApi,
  ContractHistory,
  ContractHistoryItem,
  ContractHistoryItemApi,
  ContractHistorySearchApi,
  ContractPricing,
  ContractPricingItem,
  ContractPricingRaw,
  PricingHeaderApi,
} from '../models';

const EMPTY_VALUE = '—';
const EMPTY_CELL = '—';
const HISTORY_DATE_FORMAT = 'dd-MM-yyyy HH:mm';

export const resolveEntity = async <T extends BaseOption>(
  key: string | number | null | undefined,
  serviceFind: (id: string) => Promise<T | null>,
): Promise<T | null> => {
  const stringKey = String(key ?? '').trim();

  if (!stringKey || stringKey === '-1') {
    return null;
  }

  try {
    return await serviceFind(stringKey);
  } catch {
    return null;
  }
};

const normalizePricingRow = async (key: string, pricing: PricingHeaderApi): Promise<ContractPricingItem> => {
  const [branch, carrier, service, cargoType, currency, pricingType, pricingBasis] = await Promise.all([
    resolveEntity(pricing.branchKey, branchesService.find),
    resolveEntity(pricing.carrierKey, carriersService.find),
    resolveEntity(pricing.serviceKey, carrierServicesService.find),
    resolveEntity(pricing.cargoType, cargoTypesService.find),
    resolveEntity(pricing.currencyCode, currenciesService.find),
    resolveEntity(pricing.pricingType, pricingTypesService.find),
    resolveEntity(pricing.pricingBasis, tariffTypesService.find),
  ]);

  return {
    branch,
    cargoType,
    carrier,
    currency,
    id: key,
    pricingBasis,
    pricingKey: pricing.pricingKey,
    pricingType,
    service,
  };
};

export const normalizeContractDocuments = (data: ContractDocumentsApi): ContractDocuments => ({
  items: data.items.map(
    (item): ContractDocumentItem => ({
      comment: item.comment || EMPTY_CELL,
      createdOn: item.createdOn || '',
      id: item.key,
      name: item.name,
      type: item.type || EMPTY_CELL,
    }),
  ),
  totalCount: data.totalCount,
});

export const normalizeContractPricing = async (data: ContractPricingRaw): Promise<ContractPricing> => {
  const items = await Promise.all(data.items.map(({ key, pricing }) => normalizePricingRow(key, pricing)));

  return {
    items,
    totalCount: data.totalCount,
  };
};

const normalizeHistoryRow = (
  item: ContractHistoryItemApi,
  usersById: Record<string, { fullName?: string; label?: string }>,
): ContractHistoryItem => {
  const userId = item.createdBy != null ? String(item.createdBy) : '';
  const user = usersById[userId];

  return {
    action: item.action || EMPTY_VALUE,
    comment: item.comment || EMPTY_VALUE,
    createdOn: item.createdOn ? formatDate(item.createdOn, HISTORY_DATE_FORMAT) || EMPTY_VALUE : EMPTY_VALUE,
    id: item.id || `${item.createdOn}-${item.createdBy}`,
    userName: user?.fullName || user?.label || EMPTY_VALUE,
  };
};

export const normalizeContractHistory = async (data: ContractHistorySearchApi): Promise<ContractHistory> => {
  const companyId = getSelectedCompanyId() ?? '';
  const userIds = [...new Set(data.items.map((item) => item.createdBy).filter((id): id is number => id != null))].map(
    String,
  );

  const users = userIds.length > 0 ? await companyUsersService.findAll(userIds, { companyId }) : [];
  const usersById = Object.fromEntries(users.map((user) => [String(user.id), user]));

  return {
    items: data.items.map((item) => normalizeHistoryRow(item, usersById)),
    totalCount: data.totalCount,
  };
};
